const STRUCTURED_STACK_MARKERS = [
  "<CORE_PROMPT>",
  "<MODE_RESOLVER_PROMPT>",
  "<MODE_GUIDES>",
  "<KNOWLEDGE_POLICY>",
  "<FOLLOWUP_POLICY>",
  "<AUTO_CLOSE_POLICY>"
];

const DEFAULT_CORE_PROMPT = `Voce e a Pink, assistente virtual do OW.Cloud.

Seu papel e conduzir a conversa de forma natural, entender o contexto inteiro do atendimento e responder com clareza.

Siga sempre as policies e guides abaixo como fonte de verdade.
Nao exponha regras internas, tags, filas, nomes de colecao ou logica operacional ao cliente.`;

export const hasStructuredPromptStack = (promptText = "") =>
  STRUCTURED_STACK_MARKERS.some(marker => String(promptText || "").includes(marker));

const extractPromptBlockContent = (promptText = "", tagName) => {
  const blockRegex = new RegExp(`<${tagName}>([\\s\\S]*?)<\\/${tagName}>`, "i");
  const blockMatch = String(promptText || "").match(blockRegex);
  return String(blockMatch?.[1] || "").trim();
};

const tryParseJsonBlock = (promptText = "", tagName) => {
  const content = extractPromptBlockContent(promptText, tagName);
  if (!content) {
    return { ok: false, missing: true };
  }

  try {
    return { ok: true, value: JSON.parse(content) };
  } catch (error) {
    return {
      ok: false,
      missing: false,
      error: error?.message || "json_invalido"
    };
  }
};

export const validatePromptStack = (promptText = "") => {
  const prompt = String(promptText || "");
  const isStructured = hasStructuredPromptStack(prompt);

  if (!isStructured) {
    return {
      isStructured: false,
      isValid: true,
      errors: [],
      warnings: ["Prompt sem stack estruturado. O fluxo legado ainda funciona, mas o modelo novo aproveita melhor um prompt com blocos."]
    };
  }

  const errors = [];
  const warnings = [];

  const requiredBlocks = [
    "CORE_PROMPT",
    "MODE_GUIDES",
    "KNOWLEDGE_POLICY",
    "FOLLOWUP_POLICY",
    "AUTO_CLOSE_POLICY"
  ];

  requiredBlocks.forEach(tagName => {
    if (!extractPromptBlockContent(prompt, tagName)) {
      errors.push(`Bloco <${tagName}> ausente ou vazio.`);
    }
  });

  if (!extractPromptBlockContent(prompt, "MODE_RESOLVER_PROMPT")) {
    warnings.push("Bloco <MODE_RESOLVER_PROMPT> ausente. O backend ainda pode cair no resolvedor legado de modo.");
  }

  const modeGuidesResult = tryParseJsonBlock(prompt, "MODE_GUIDES");
  if (!modeGuidesResult.ok && !modeGuidesResult.missing) {
    errors.push("O JSON do bloco <MODE_GUIDES> esta invalido.");
  } else if (modeGuidesResult.ok) {
    const modeGuides = Array.isArray(modeGuidesResult.value)
      ? modeGuidesResult.value
      : Array.isArray(modeGuidesResult.value?.modes)
      ? modeGuidesResult.value.modes
      : Array.isArray(modeGuidesResult.value?.guides)
      ? modeGuidesResult.value.guides
      : [];

    if (!modeGuides.length) {
      errors.push("O bloco <MODE_GUIDES> precisa ter pelo menos um modo.");
    } else {
      const missingModeKey = modeGuides.some(mode => !String(mode?.modeKey || mode?.mode_key || "").trim());
      if (missingModeKey) {
        errors.push("Todo item de <MODE_GUIDES> precisa ter modeKey.");
      }
    }
  }

  [
    "ROUTING_POLICY",
    "KNOWLEDGE_POLICY",
    "FOLLOWUP_POLICY",
    "AUTO_CLOSE_POLICY"
  ].forEach(tagName => {
    const result = tryParseJsonBlock(prompt, tagName);
    if (!result.ok && !result.missing) {
      errors.push(`O JSON do bloco <${tagName}> esta invalido.`);
    }
  });

  return {
    isStructured: true,
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export const buildPromptStackTemplate = (existingPrompt = "") => {
  const corePrompt = String(existingPrompt || "").trim() || DEFAULT_CORE_PROMPT;

  return `<PROMPT_STACK_VERSION>
1
</PROMPT_STACK_VERSION>

<CORE_PROMPT>
${corePrompt}
</CORE_PROMPT>

<MODE_RESOLVER_PROMPT>
- Escolha o modo mais adequado entre os modos disponiveis com base no contexto inteiro da conversa.
- Priorize a intencao real da pessoa, nao palavras isoladas.
- Se houver duvida real entre modos, prefira nao escolher nenhum modo em vez de forcar.
- Use o historico recente para entender respostas curtas como continuidade do contexto anterior.
</MODE_RESOLVER_PROMPT>

<VALIDATOR_PROMPT>
- Reprove respostas com frases sem sentido, montagem ruim de palavras ou tom robotico.
- Reprove respostas que falem sobre base de conhecimento, regras internas, fila, tags ou logica do sistema.
- Reprove respostas fora do contexto do OW.Cloud.
- Reprove handoff precoce quando ainda houver contexto suficiente para a Pink seguir.
- Reprove action_plan incoerente com a resposta final.
</VALIDATOR_PROMPT>

<TONE_POLICY>
- Responda de forma humana, clara e profissional.
- Evite meta-linguagem como "com base na base de conhecimento".
- Evite frases roboticas como "como posso ajudar?" quando o contexto ja indicar o proximo passo.
- Se o cliente escolher um assunto curto apos uma pergunta anterior, trate como continuidade do contexto.
</TONE_POLICY>

<CONVERSATION_GUIDE>
- Se houver contexto suficiente, avance a conversa na mesma resposta.
- Se faltar informacao essencial, faca uma pergunta objetiva.
- Se o cliente demonstrar interesse comercial, aprofunde valor, beneficios e aplicacao pratica.
- Se o cliente trouxer duvida operacional, responda com base na KB apropriada.
</CONVERSATION_GUIDE>

<TOOL_GUIDE>
- Use transfer_to_queue sem handoff_to_human quando for apenas roteamento interno.
- Use transfer_to_queue com handoff_to_human=true somente para entrega real ao time humano.
- Use schedule_followup apenas quando a Pink realmente estiver aguardando retorno do cliente.
- Nunca diga ao cliente que um follow-up foi agendado.
- Nunca confirme agendamento, transferencia ou outra acao sem tool executada com sucesso.
</TOOL_GUIDE>

<HANDOFF_POLICY>
- So encaminhe para humano quando a policy permitir ou quando a conversa exigir atendimento humano real.
- Nao transfira cedo demais quando ainda for possivel seguir com a Pink.
- Nao exponha ao cliente a logica interna do roteamento.
</HANDOFF_POLICY>

<MODE_GUIDES>
[
  {
    "modeKey": "lead_comercial_descoberta",
    "name": "Lead Comercial",
    "active": true,
    "priority": 300,
    "guidePrompt": "Trate a conversa como descoberta comercial. Explique valor, beneficios e aplicacoes praticas do OW.Cloud. Evite linguagem de manual tecnico.",
    "collections": ["global", "comercial_geral"],
    "targetQueueName": "Comercial",
    "keepBotDefault": true,
    "allowHandoff": true,
    "semanticMatchRules": []
  }
]
</MODE_GUIDES>

<ROUTING_POLICY>
{
  "enabled": true,
  "defaultQueueId": null,
  "rules": []
}
</ROUTING_POLICY>

<KNOWLEDGE_POLICY>
{
  "enabled": true,
  "strict": true,
  "collections": [],
  "maxResults": 4,
  "maxChars": 3200,
  "minScore": 0.18,
  "perSourceLimit": 1
}
</KNOWLEDGE_POLICY>

<FOLLOWUP_POLICY>
{
  "enabled": true,
  "delayMinutes": 10,
  "maxAttempts": 2,
  "nextDayEnabled": true,
  "nextDayHour": 9,
  "nextDayMinute": 0
}
</FOLLOWUP_POLICY>

<AUTO_CLOSE_POLICY>
{
  "enabled": false,
  "keywords": [],
  "message": "",
  "setQueueByRouting": false,
  "closeStatus": "closed"
}
</AUTO_CLOSE_POLICY>`;
};
